import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { AxiosError } from 'axios';
import { apiClient, type RequestBody } from '../client';
import { queryKeys } from '../react-query.config';
import { toast } from '@/hooks/use-toast';

export const useSubmitSelections = ({
  pickemId,
  isUpdate,
  onSuccess,
  showToast = true,
}: {
  pickemId: string;
  isUpdate?: boolean;
  onSuccess?: () => void;
  showToast?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [ipBlockedError, setIpBlockedError] = useState<string | null>(null);

  type SelectionPayload = RequestBody<'/api/pickems/{id}/selections', 'post'>;

  const mutation = useMutation({
    mutationKey: ['submitSelections', pickemId],
    mutationFn: async (data: SelectionPayload) => {
      // Clear previous IP blocked error
      setIpBlockedError(null);

      return apiClient.post('/api/pickems/{id}/selections', data, {
        pathParams: { id: pickemId },
      });
    },
    onSuccess: async _data => {
      // Refetch the specific pickem detail
      await queryClient.refetchQueries({
        queryKey: queryKeys.pickem.detail(pickemId),
      });

      // Refetch (not just invalidate) all pickems to ensure has_entered is updated
      // This prevents race condition where UI shows stale data before DB update completes
      await queryClient.refetchQueries({
        queryKey: queryKeys.pickem.all,
      });

      if (showToast) {
        toast({
          title: 'Success',
          description: isUpdate
            ? 'Your selections have been updated successfully.'
            : 'Your selections have been submitted successfully.',
        });
      }

      onSuccess?.();
    },
    onError: (error: any) => {
      // Extract backend error field from original AxiosError
      const backendError = (error.originalError as AxiosError)?.response?.data as {
        error?: string;
        message?: string;
      };

      // Check if this is an IP validation error (403 with specific error code)
      if (error.statusCode === 403 && backendError?.error === 'IP_NOT_WHITELISTED') {
        setIpBlockedError(
          error.message || 'Contest submissions are only available from Delta in-flight networks'
        );
        // Do NOT show toast for IP blocked error (full-screen dialog will handle it)
        return;
      }

      // Show toast for other errors
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit selections. Please try again.',
        variant: 'destructive',
      });
    },
  });

  return {
    ...mutation,
    ipBlockedError,
    clearIpBlockedError: () => setIpBlockedError(null),
  };
};
